<template>

  <v-container fluid class="ma-0 pa-0 license-manager">

    <!------------------------>
    <!-- Add License Action -->
    <!------------------------>
    <v-row class="ma-0 pa-0 mb-4" v-if="!showForm">
      <v-btn text color="primary" @click="renderForm">
        + Add License
      </v-btn>
    </v-row>

    <!------------------------->
    <!-- Active License Info -->
    <!------------------------->
    <template v-else>
      <v-form ref="form" v-model="isFormValid" lazy-validation>
        <v-row class="pa-0 mt-2 mb-6 license-container">
          <v-col class="pt-2 pr-2 pb-4 pl-4 ma-0">

            <!-- Address -->
            <template v-if="showAddressFields">
              <v-row class="pa-0 ma-0 mt-3 mb-2">
                <div class="section-title">Ship-to address:</div>
              </v-row>
              <template v-if="categorySelections">
                <v-row class="pa-0 ma-0">
                  <v-col sm="10" class="ma-0 pa-0 mt-3 field-label">
                    Address type:
                  </v-col>
                </v-row>
                <v-row class="pa-0 ma-0 mb-2">
                  <v-radio-group
                      row
                      required
                      :rules="$root.rules.fieldRequired"
                      @change="setDirty(`${licenseType}_category`)"
                      v-model="itemToEdit[`${licenseType}_category`]">
                    <v-radio
                        v-for="category in categorySelections"
                        :key="category.code"
                        :label="category.label"
                        :value="category.code" />
                  </v-radio-group>
                </v-row>
              </template>
              <v-row class="pa-0 ma-0 mt-3">
                <v-col cols="12" xs="12" sm="11" md="6" lg="5" class="ma-0 pa-0">
                  <v-text-field
                      required
                      :rules="$root.rules.fieldRequired"
                      label="Street 1"
                      hide-details="auto"
                      @input="setDirty('street_1')"
                      v-model.trim="itemToEdit.street_1" />
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0 mt-3">
                <v-col cols="12" xs="12" sm="11" md="6" lg="5" class="ma-0 pa-0">
                  <v-text-field
                      label="Street 2"
                      hide-details="auto"
                      @input="setDirty('street_2')"
                      v-model.trim="itemToEdit.street_2" />
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0 mt-3 mr-6">
                <v-col cols="12" xs="12" sm="8" md="4" lg="4" class="ma-0 pa-0">
                  <v-text-field
                      required
                      :rules="$root.rules.fieldRequired"
                      label="City"
                      hide-details="auto"
                      @input="setDirty('city')"
                      v-model.trim="itemToEdit.city" />
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0 mt-3 mr-6">
                <v-col cols="12" xs="12" sm="5" md="4" lg="3" class="ma-0 pa-0">
                  <v-select
                      required
                      :rules="$root.rules.fieldRequired"
                      label="State"
                      :items="stateValueList"
                      item-text="label"
                      item-value="code"
                      @input="setDirty('state')"
                      v-model="itemToEdit.state" />
                </v-col>
              </v-row>
              <v-row class="pa-0 ma-0 mr-6 mb-10">
                <v-col cols="12" xs="12" sm="5" md="4" lg="3" class="ma-0 pa-0">
                  <v-text-field
                      required
                      :rules="$root.rules.fieldRequired"
                      label="Zip"
                      hide-details="auto"
                      @input="setDirty('zip')"
                      v-model.trim="itemToEdit.zip" />
                </v-col>
              </v-row>
            </template>

            <!-- License Info -->
            <v-row class="pa-0 ma-0 mt-4 mb-4">
              <div class="section-title">License Info:</div>
            </v-row>
            <v-row class="pa-0 ma-0 mt-7 mr-6 mb-3">
              <v-col cols="12" xs="12" sm="11" md="6" lg="4" class="ma-0 pa-0">
                <v-text-field
                    required
                    :rules="$root.rules.fieldRequired"
                    outlined
                    label="License number"
                    @input="setDirty(`${licenseType}_number`)"
                    v-model.trim="itemToEdit[`${licenseType}_number`]" />
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0">
              <v-col cols="12" xs="12" sm="4" md="3" lg="2" class="pa-0 ma-0 d-flex align-center field-label">
                <div :class="{ 'field-missing': missingIssueDate }">Issue date</div>
                <div class="mt-1 field-missing-message" v-if="missingIssueDate">Field is required</div>
              </v-col>
              <v-col cols="12" xs="12" sm="8" md="9" lg="10" class="pa-0 ma-0 d-flex align-center">
                {{ formatDate(itemToEdit[`${licenseType}_issue_date`]) }}
                <v-btn text class="ml-1" @click="openDateSelector('issue_date')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0 mt-3">
              <v-col cols="12" xs="12" sm="4" md="3" lg="2" class="pa-0 ma-0 d-flex align-center field-label">
                <div :class="{ 'field-missing': missingExpiration }">Expiration</div>
                <div class="mt-1 field-missing-message" v-if="missingExpiration">Field is required</div>
              </v-col>
              <v-col cols="12" xs="12" sm="8" md="9" lg="10" class="pa-0 ma-0 d-flex align-center">
                {{ formatDate(itemToEdit[`${licenseType}_expiration`]) }}
                <v-btn text class="ml-1" @click="openDateSelector('expiration')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row class="pa-0 ma-0 mt-3">
              <v-col cols="12" xs="12" sm="4" md="3" lg="2" class="pa-0 ma-0 d-flex align-center field-label">
                <div>Amended date (optional)</div>
              </v-col>
              <v-col cols="12" xs="12" sm="8" md="9" lg="10" class="pa-0 ma-0 d-flex align-center">
                {{ formatDate(itemToEdit[`${licenseType}_amended_date`]) }}
                <v-btn text class="ml-1" @click="openDateSelector('amended_date')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-tooltip bottom v-if="itemToEdit[`${licenseType}_amended_date`]">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn text class="ml-2" v-bind="attrs" v-on="on" @click="clearSelectedDate('amended_date')">
                      <v-icon color="red lighten-1">mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <div>Clear date</div>
                </v-tooltip>
              </v-col>
            </v-row>

            <!-- (document loading) -->
            <template v-if="isDocLoading">
              <v-row class="pa-0 ma-0">
                <v-col class="d-flex align-center justify-center" style="height:120px">
                  <v-progress-circular indeterminate :size="40" color="primary" />
                </v-col>
              </v-row>
            </template>

            <!-- Change Attachment Action -->
            <template v-else>
              <v-row class="ma-0 pa-0 mt-8 mb-4" align="center" justify="center">
                <v-btn
                    v-if="hasSavedLicenseDoc && !showHandler"
                    text
                    color="primary"
                    @click="changeAttachment">
                  Change Document
                </v-btn>
              </v-row>
            </template>

            <v-row class="ma-0 pa-0 mt-4 mb-2" v-if="showHandler">
              <v-col cols="12" xs="12" sm="12" md="8" lg="7" class="pa-0 ma-0">
                <v-file-input
                    required
                    :rules="$root.rules.fieldRequired"
                    label="Select File"
                    :accept="allowedFileTypes"
                    class="mt-1 mr-10"
                    prepend-icon="mdi-file-document"
                    @click:clear="clearAttachment"
                    @change="onAttachmentChange" />
              </v-col>
            </v-row>

            <!-- Rendered Attachment -->
            <div class="mr-3 file-preview-container">
              <embed ref="attachment" />
            </div>

          </v-col>
        </v-row>
      </v-form>
    </template>

    <!------------------------------------>
    <!-- Date Selector (for both dates) -->
    <!------------------------------------>
    <v-dialog persistent max-width="350" v-model="isDateSelectorOpen">
      <v-card>
        <v-card-text class="date-picker-body">
          <v-date-picker v-model="selectedDate" />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="cancelDateSelection">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="setSelectedDate">
            Select
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
/* eslint-disable no-unneeded-ternary */
/* global moment */
export default {
  name: 'LicenseManager',

  props: {
    licenseType: {
      type: String,
      required: true
    },
    item: {
      type: Object,
      required: true
    },
    itemToEdit: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    showAddressFields: {
      type: Boolean,
      default: true
    },
    allowedFileTypes: {
      type: String,
      default: 'image/*'
    },
    fileDisplayWidth: {
      type: String,
      default: '240'
    },
    docDisplayHeight: {
      type: String,
      default: '300'
    }
  },

  data () {
    return {
      isFormValid: true,
      missingIssueDate: false,
      missingExpiration: false,
      displayNewForm: false,
      showHandler: false,
      locationCopy: null, // a copy of the original location data
      // ------- dirty management
      isDirty: false,
      dirtyProps: [],
      // ------ license doc
      isDocLoading: false,
      file: null,
      fileData: null,
      // licenseRules: [
      //   v => /^$|^([0-9]|[a-z]|[A-Z]){20}$/.test(v) || 'License number must be exactly 20 alpha-numeric characters'
      // ],
      // ------ date selection
      isDateSelectorOpen: false,
      selectedDateFor: null,
      selectedDate: null
    }
  },

  computed: {
    userSettings () {
      return this.$store.getters['app/settings']
    },
    showForm () {
      return this.hasBeenConfigured || this.displayNewForm
    },
    hasBeenConfigured () {
      switch (this.licenseType) {
        case 'establishment_license': {
          return (
            this.itemToEdit.establishment_license_number ||
            this.itemToEdit.establishment_license_issue_date ||
            this.itemToEdit.establishment_license_expiration
          ) ? true : false
        }
        case 'drc': {
          return (
            this.itemToEdit.drc_number ||
            this.itemToEdit.drc_issue_date ||
            this.itemToEdit.drc_expiration
          ) ? true : false
        }
        default: return false
      }
    },
    hasSavedLicenseDoc () {
      return this.itemToEdit[`saved_${this.licenseType}`]
    },
    stateValueList () {
      return this.$store.getters['app/datasetUsStates']
    },
    categorySelections () {
      // establishment license
      if (this.licenseType === 'establishment_license') {
        return [
          { label: 'Retail', code: 'retail' },
          { label: 'Manufacturing Facility', code: 'manufacturing_facility' },
          { label: 'Offsite', code: 'offsite' }
        ]
      }
      // drc
      if (this.licenseType === 'drc') {
        return [
          { label: 'Retail', code: 'retail' },
          { label: 'Offsite Cultivation', code: 'offsite_cultivation' }
        ]
      }
      return null
    }
  },

  watch: {
    // Set dirty state for address switch
    showAddressFields: function (updated) {
      // NOTE - Only set dirty for one license as they both share one state - |M|
      if (this.licenseType === 'establishment_license') this.setDirty('switched_address')
    }
  },

  beforeMount () {
    this.loadSavedLicenseDoc()
  },

  beforeDestroy () {
    this.reset()
  },

  methods: {
    getUpdatedLicenseDoc () {
      return (this.file)
        ? {
          asset_key: this.licenseType,
          file: this.file,
          file_name: this.fileName,
          file_type: this.fileType,
          file_extension: this.fileExtension
        }
        : null
    },

    async validate () {
      this.isFormValid = true // reset
      await this.$refs.form.validate()
      if (!this.itemToEdit[`${this.licenseType}_issue_date`]) {
        this.missingIssueDate = true
        this.isFormValid = false
      } else {
        this.missingIssueDate = false
      }
      if (!this.itemToEdit[`${this.licenseType}_expiration`]) {
        this.missingExpiration = true
        this.isFormValid = false
      } else {
        this.missingExpiration = false
      }
      return this.isFormValid
    },

    // Manage dirty state
    setDirty (propName) {
      let isDiff = false
      if (propName.endsWith('_license_issue_date') || propName.endsWith('_license_expiration') || propName.endsWith('_amended_date')) {
        isDiff = (!this.item[propName] && !this.itemToEdit[propName])
          ? false
          : !moment(this.item[propName]).tz(this.userSettings.timezone).isSame(moment(this.itemToEdit[propName]).tz(this.userSettings.timezone))
      } else if (propName === 'switched_address') {
        isDiff = !((this.licenseType === this.item.license_for_shipping_address) && this.showAddressFields)
      } else if (propName === 'changed_file') {
        isDiff = true
      } else {
        isDiff = this.item[propName] !== this.itemToEdit[propName]
      }

      if (isDiff) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
        this.$emit('dirty', this.dirtyProps)
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
        this.$emit('dirty', this.dirtyProps)
      }
    },

    async loadSavedLicenseDoc () {
      if (this.hasSavedLicenseDoc) {
        this.isDocLoading = true
        this.fileData = await this.$root.fetchLocationAsset(this.itemToEdit.id, this.licenseType)
        this.renderAttachment(this.fileData)
        this.isDocLoading = false
      } else {
        this.showHandler = true
      }
    },

    reset () {
      this.displayNewForm = false
      this.missingIssueDate = false
      this.missingExpiration = false
      this.file = null
      this.fileName = null
      this.fileExtension = null
      this.fileType = null
      this.fileData = null
    },

    renderForm () {
      this.displayNewForm = true
    },

    changeAttachment () {
      this.clearAttachment()
      this.showHandler = true
    },

    formatDate (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MM/DD/YYYY') : ''
    },

    openDateSelector (fieldName) {
      this.selectedDateFor = fieldName
      this.isDateSelectorOpen = true
    },

    cancelDateSelection () {
      // Clear selections and close modal
      this.selectedDateFor = null
      this.selectedDate = null
      this.isDateSelectorOpen = false
    },

    setSelectedDate () {
      const dateFor = this.selectedDateFor
      const date = this.selectedDate
      this.itemToEdit[`${this.licenseType}_${dateFor}`] = moment(date).tz(this.userSettings.timezone)
      this.setDirty(`${this.licenseType}_${dateFor}`)

      // Clear selections and close modal
      this.selectedDateFor = null
      this.selectedDate = null
      this.isDateSelectorOpen = false
    },

    clearSelectedDate (dateFor) {
      this.itemToEdit[`${this.licenseType}_${dateFor}`] = null
      this.setDirty(`${this.licenseType}_${dateFor}`)
    },

    clearAttachment () {
      this.file = null
      this.fileName = null
      this.fileExtension = null
      this.fileType = null
      this.fileData = null
      const docObj = this.$refs.attachment
      if (docObj) {
        docObj.src = null
        docObj.width = 0
        docObj.height = 0
      }
    },

    renderAttachment (data) {
      const docObj = this.$refs.attachment
      if (!docObj || !data) return false

      // Extract doc type (only works for new file attachment)
      const docType = data.split(';')[0].substring(5)
      // console.log('[DEVING] extracted docType:', docType)
      // #toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0

      // Render image without height (if we can determine it)
      if (docType.startsWith('image/')) {
        docObj.src = data
        docObj.width = this.fileDisplayWidth

      // Otherwise presume a doc (e.g. PDF)
      } else {
        docObj.src = `${data}#toolbar=0&navpanes=0&scrollbar=0`
        docObj.width = this.fileDisplayWidth
        docObj.height = this.docDisplayHeight
      }
    },

    onAttachmentChange (file) {
      if (!file) return false

      // Save file and process data for preview
      this.file = file
      this.fileName = file.name
      this.fileType = file.type
      this.fileExtension = file.name.substring((file.name.lastIndexOf('.') + 1))

      // Set dirty
      this.setDirty('changed_file')

      const reader = new FileReader()
      reader.onload = this.processFile
      reader.readAsDataURL(this.file)
    },

    processFile (event) {
      if (event) {
        this.fileData = event.target.result
        this.renderAttachment(this.fileData)
      }
    }
  }
}
</script>

<style scoped>
  .license-manager .license-container {
    border: 1px solid #525252;
    border-radius: 4px;
  }

  .license-manager .section-title {
    color: #f8f8f8;
    font-size: 1.15em;
    font-style: italic;
  }

  .license-manager .field-label {
    color: #9a9a9a;
  }

  .license-manager .field-missing {
    color: #ff5252;
  }

  .license-manager .field-missing-message {
    font-size: 12px;
    color: #ff5252;
  }

  .license-manager .file-preview-container {
    text-align: center;
  }
</style>
